/**
 * Config model for the Big Winners feature card.
 *
 * This is intentionally a plain, serializable shape so it can be dropped onto
 * a real read/write hook once the backend endpoint lands. Until then the card
 * manages it in local state seeded from `DEFAULT_BIG_WINNERS_CONFIG`.
 */
export type DurationUnit = "hours" | "days";

export interface BigWinnersConfig {
  /** Operator-facing on/off for the casino-home Big Winners carousel. */
  enabled: boolean;
  /** Win amount (in platform currency) that qualifies as a "big win". */
  threshold: number | null;
  /** Maximum number of wins surfaced in the carousel. */
  maxItems: number | null;
  /** How far back we look for qualifying wins. */
  timeWindowValue: number | null;
  timeWindowUnit: DurationUnit;
  /** How often the carousel refreshes. */
  refreshIntervalValue: number | null;
  refreshIntervalUnit: DurationUnit;
}

export const DEFAULT_BIG_WINNERS_CONFIG: BigWinnersConfig = {
  enabled: false,
  threshold: null,
  maxItems: null,
  timeWindowValue: null,
  timeWindowUnit: "hours",
  refreshIntervalValue: null,
  refreshIntervalUnit: "hours",
};

/** Shallow value-equality for two configs (used for dirty tracking). */
export function isBigWinnersConfigEqual(
  a: BigWinnersConfig,
  b: BigWinnersConfig,
): boolean {
  return (
    a.enabled === b.enabled &&
    a.threshold === b.threshold &&
    a.maxItems === b.maxItems &&
    a.timeWindowValue === b.timeWindowValue &&
    a.timeWindowUnit === b.timeWindowUnit &&
    a.refreshIntervalValue === b.refreshIntervalValue &&
    a.refreshIntervalUnit === b.refreshIntervalUnit
  );
}

/**
 * Parse a raw input string into a non-negative integer, or `null` when empty.
 * Returns the previous value untouched semantics to the caller by yielding
 * `null` for blanks and ignoring non-numeric noise.
 */
export function parseNonNegativeInt(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.floor(n);
}

/**
 * A config is valid to save when every *provided* numeric field is a
 * non-negative number. Blank (null) fields are allowed — the backend applies
 * its own defaults — so scaffolding never blocks on partially filled forms.
 */
export function isBigWinnersConfigValid(config: BigWinnersConfig): boolean {
  const numericFields = [
    config.threshold,
    config.maxItems,
    config.timeWindowValue,
    config.refreshIntervalValue,
  ];
  return numericFields.every(
    (v) => v === null || (Number.isFinite(v) && (v as number) >= 0),
  );
}
